import { useParams, useNavigate } from "react-router-dom";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import "../../styles/timeline.css";
import { useGetLevelByIdQuery } from "../../store/api/masterCourseApi";
import { useState } from "react";

const SOFT_LOCK_FALLBACK_MESSAGE =
  "Thank you for your time and dedication to Module 1 of this course. The remaining half of this module will be unlocked on Tuesday 18th November, with content specifically customised to your organisation.";

export const useCourseHook = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [softLockMessage, setSoftLockMessage] = useState<string | null>(null);
  const [showSoftLockModal, setShowSoftLockModal] = useState(false);

  const {
    data: levelData,
    isLoading: isLevelLoading,
    error: levelError,
  } = useGetLevelByIdQuery(id || "", {
    skip: !id,
  });

  const fetchError = levelError as FetchBaseQueryError | undefined;
  const demoErrorCode =
    fetchError && fetchError.status === 403 && fetchError.data && typeof fetchError.data === "object"
      ? (fetchError.data as { error_code?: string }).error_code
      : null;

  const isLoading = isLevelLoading;
  const hasError =
    (!!levelError && !demoErrorCode) || (!levelData && !isLevelLoading && !demoErrorCode);

  // Debug logging for Level fetch issues
  if (levelError) {
    console.error('Level fetch error:', {
      id,
      status: fetchError?.status,
      data: fetchError?.data,
      error: levelError
    });
  }

  const openSoftLockModal = (message?: string | null) => {
    const trimmed = typeof message === "string" ? message.trim() : "";
    setSoftLockMessage(trimmed || SOFT_LOCK_FALLBACK_MESSAGE);
    setShowSoftLockModal(true);
  };

  const closeSoftLockModal = () => {
    setShowSoftLockModal(false);
  };

  const handleLessonClick = (event: any, lesson: any) => {
    if (levelData?.softLock) {
      const limit = levelData.softLock.afterLessonOrder;
      if (
        typeof lesson?.order === "number" &&
        lesson.order > limit &&
        lesson.status !== "completed"
      ) {
        openSoftLockModal(levelData.softLock.message);
        return;
      }
    }
    if (lesson.status === "unlocked" || lesson.status === "completed") {
      navigate(`/lesson/${lesson.id}`);
    }
  };

  // Normalize levelData to courseDetails format for backwards compatibility
  const courseDetails = levelData ? {
    id: levelData.id,
    title: levelData.title,
    description: levelData.description || "",
    progress_percentage: levelData.progress_percentage ?? levelData.progressPercentage ?? 0,
    is_completed: levelData.is_completed ?? levelData.isCompleted ?? false,
    icon_uri: levelData.icon_uri ?? levelData.iconUri ?? null,
    reward_xp: levelData.reward_xp ?? levelData.rewardXp ?? 0,
    estimated_total_duration_minutes: levelData.estimated_total_duration_minutes ?? levelData.estimatedTotalDurationMinutes ?? 0,
    levelNumber: levelData.levelNumber ?? 1,
    softLock: levelData.softLock ? {
      afterLessonOrder: levelData.softLock.afterLessonOrder,
      message: levelData.softLock.message ?? null,
    } : undefined,
    lessons: levelData.lessons || [],
  } : undefined;

  return {
    courseId: id,
    courseDetails,
    lessons: levelData?.lessons || [],
    isLoading,
    hasError,
    demoErrorCode,
    handleLessonClick,
    showSoftLockModal,
    softLockMessage,
    closeSoftLockModal,
    openSoftLockModal,
  };
};
